import React from "react";
import {
  Paper,
  Stack,
  Typography,
  Box,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DataCoverageService } from "../../../services/dataCoverageService";
import { apiService } from "../../../config/api";
import { COMMON_FITBIT_HEALTHKIT_METRIC_KEYS } from "../../../constants/dataCoverageCommonMetrics";
import ParticipantCoverageFilters, {
  DatePreset,
} from "./ParticipantCoverageFilters";
import {
  getMetricsForDeviceType,
  METRIC_LABELS,
} from "../../../constants/metricCategories";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import type { StudyDeviceAvailability } from "./studyDeviceAvailability";

interface StudyLevelMetricCoverageHeatmapCardProps {
  studyId: string;
  deviceAvailability: StudyDeviceAvailability;
}

interface MetricOption {
  key: string;
  label: string;
}

interface HeatmapResult {
  metricLabels: string[];
  periodLabels: string[];
  coverageMatrix: number[][];
}

interface StudyFailedFitbitRefresh {
  participantId: string;
  participantName: string;
  ranges: { startDate: string; endDate: string; dayCount: number }[];
}

const COMMON_METRIC_KEY_SET: Set<string> = new Set(
  COMMON_FITBIT_HEALTHKIT_METRIC_KEYS as readonly string[],
);

const StudyLevelMetricCoverageHeatmapCard: React.FC<
  StudyLevelMetricCoverageHeatmapCardProps
> = ({ studyId, deviceAvailability }) => {
  const [fromDate, setFromDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [preset, setPreset] = React.useState<DatePreset | null>("7d");
  const [deviceType, setDeviceType] = React.useState<
    "fitbit" | "healthkit" | "both"
  >("both");
  const [availableMetrics, setAvailableMetrics] = React.useState<
    MetricOption[]
  >([]);
  const [selectedMetrics, setSelectedMetrics] = React.useState<Set<string>>(
    new Set(),
  );
  const [aggregationType, setAggregationType] = React.useState<
    "daily" | "weekly" | "monthly" | "yearly"
  >("monthly");
  const [heatmapRunning, setHeatmapRunning] = React.useState(false);
  const [heatmapError, setHeatmapError] = React.useState<string | null>(null);
  const [heatmapData, setHeatmapData] = React.useState<HeatmapResult | null>(
    null,
  );
  const [progressStatus, setProgressStatus] = React.useState("");
  const [metricsExpanded, setMetricsExpanded] = React.useState(false);
  const [fetchStatus, setFetchStatus] = React.useState<string | null>(null);
  const [failedFitbitRefreshes, setFailedFitbitRefreshes] = React.useState<
    StudyFailedFitbitRefresh[]
  >([]);

  const hasFitbitParticipants = deviceAvailability.fitbitParticipants > 0;
  const hasHealthkitParticipants = deviceAvailability.healthkitParticipants > 0;
  const canSelectBoth = hasFitbitParticipants && hasHealthkitParticipants;

  React.useEffect(() => {
    if (deviceType === "fitbit" && !hasFitbitParticipants) {
      if (hasHealthkitParticipants) {
        setDeviceType("healthkit");
      } else {
        setDeviceType("both");
      }
      return;
    }

    if (deviceType === "healthkit" && !hasHealthkitParticipants) {
      if (hasFitbitParticipants) {
        setDeviceType("fitbit");
      } else {
        setDeviceType("both");
      }
      return;
    }

    if (deviceType === "both" && !canSelectBoth) {
      if (hasFitbitParticipants) {
        setDeviceType("fitbit");
      } else if (hasHealthkitParticipants) {
        setDeviceType("healthkit");
      }
    }
  }, [
    canSelectBoth,
    deviceType,
    hasFitbitParticipants,
    hasHealthkitParticipants,
  ]);

  // Initialize dates on mount based on default preset
  React.useEffect(() => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    setFromDate(start);
    setEndDate(end);
  }, []);

  const applyPreset = (nextPreset: DatePreset | null) => {
    setPreset(nextPreset);
    if (!nextPreset) return;

    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    let start = new Date(today);

    if (nextPreset === "7d") {
      start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
    } else if (nextPreset === "1m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 1);
      start.setDate(start.getDate() + 1);
    } else if (nextPreset === "6m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 6);
      start.setDate(start.getDate() + 1);
    } else {
      start = new Date(today);
      start.setFullYear(start.getFullYear() - 1);
      start.setDate(start.getDate() + 1);
    }

    setFromDate(start.toISOString().slice(0, 10));
    setEndDate(end);
  };

  // Update available metrics when device type changes
  React.useEffect(() => {
    let metrics: MetricOption[] = [];

    if (deviceType === "both") {
      metrics = COMMON_FITBIT_HEALTHKIT_METRIC_KEYS.map((key) => ({
        key,
        label: METRIC_LABELS[key] || key,
      }));
    } else {
      const metricKeys = getMetricsForDeviceType(deviceType);
      metrics = metricKeys.map((key) => ({
        key,
        label: METRIC_LABELS[key] || key,
      }));
    }

    setAvailableMetrics(metrics);
    setSelectedMetrics(new Set());
    setHeatmapData(null);
  }, [deviceType]);

  const toggleMetric = (metricKey: string, checked: boolean) => {
    const newSelected = new Set(selectedMetrics);
    if (checked) {
      newSelected.add(metricKey);
    } else {
      newSelected.delete(metricKey);
    }
    setSelectedMetrics(newSelected);
  };

  const selectAll = () => {
    setSelectedMetrics(new Set(availableMetrics.map((m) => m.key)));
  };

  const selectCommonOnly = () => {
    setSelectedMetrics(
      new Set(
        availableMetrics
          .filter((metric) => COMMON_METRIC_KEY_SET.has(metric.key))
          .map((metric) => metric.key),
      ),
    );
  };

  const deselectAll = () => {
    setSelectedMetrics(new Set());
  };

  const commonMetricCount = availableMetrics.filter((metric) =>
    COMMON_METRIC_KEY_SET.has(metric.key),
  ).length;

  const handleGenerateHeatmap = async () => {
    if (!fromDate || !endDate) {
      setHeatmapError("Please select both from and end dates.");
      return;
    }

    if (selectedMetrics.size === 0) {
      setHeatmapError("Please select at least one metric.");
      return;
    }

    setHeatmapRunning(true);
    setHeatmapError(null);
    setHeatmapData(null);
    setProgressStatus("Generating heatmap...");
    setFetchStatus(null);
    setFailedFitbitRefreshes([]);

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        setHeatmapError("Sign in to generate heatmap.");
        return;
      }

      setProgressStatus("Fetching coverage data...");

      const response = await DataCoverageService.getStudyMetricCoverageHeatmap(
        studyId,
        {
          fromDate,
          endDate,
          aggregationType,
          selectedMetrics: Array.from(selectedMetrics),
          deviceType,
          refreshIfMissing: true,
        },
        token,
      );

      if (response.success && (response as any).data) {
        setHeatmapData((response as any).data.heatmap);
        setFetchStatus((response as any).data?.fetch?.statusMessage || null);
        setFailedFitbitRefreshes(
          (response as any).data?.fetch?.failedFitbitRefreshes || [],
        );
        setProgressStatus("");
      } else {
        setHeatmapError("Failed to generate heatmap.");
      }
    } catch (err: any) {
      setHeatmapError(err?.message || "Error generating heatmap.");
    } finally {
      setHeatmapRunning(false);
      setProgressStatus("");
    }
  };

  const renderHeatmap = () => {
    if (!heatmapData) return null;

    const { metricLabels, periodLabels, coverageMatrix } = heatmapData;

    if (coverageMatrix.length === 0 || coverageMatrix[0].length === 0) {
      return (
        <Alert severity="info">No data available for selected criteria.</Alert>
      );
    }

    const seriesData: Array<[number, number, number]> = [];
    coverageMatrix.forEach((row, rowIndex) => {
      row.forEach((value, columnIndex) => {
        seriesData.push([columnIndex, rowIndex, value]);
      });
    });

    const option: EChartsOption = {
      backgroundColor: "#ffffff",
      grid: {
        left: 150,
        right: 40,
        top: 60,
        bottom: 110,
        containLabel: true,
      },
      tooltip: {
        position: "top",
        formatter: (params: any) => {
          const [xIndex, yIndex, value] = params.data as [
            number,
            number,
            number,
          ];
          const metric = metricLabels[yIndex] || "";
          const period = periodLabels[xIndex] || "";
          return `${metric}<br/>${period}<br/><b>Coverage:</b> ${Number(value).toFixed(1)}%`;
        },
      },
      xAxis: {
        type: "category",
        data: periodLabels,
        splitArea: { show: true },
        axisLabel: {
          rotate: 45,
          interval: 2,
          fontSize: 10,
        },
        axisTick: { alignWithLabel: true },
      },
      yAxis: {
        type: "category",
        data: metricLabels,
        splitArea: { show: true },
        axisLabel: {
          fontSize: 11,
          width: 190,
          overflow: "truncate",
        },
      },
      visualMap: {
        min: 0,
        max: 100,
        calculable: true,
        orient: "horizontal",
        left: "center",
        bottom: 20,
        text: ["100%", "0%"],
        inRange: {
          color: ["#d73027", "#fee08b", "#1a9850"],
        },
      },
      series: [
        {
          type: "heatmap",
          data: seriesData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: "rgba(0, 0, 0, 0.25)",
            },
          },
          itemStyle: {
            borderRadius: 4,
            borderColor: "#ffffff",
            borderWidth: 1,
          },
          label: {
            show: false,
          },
        },
      ],
    };

    return (
      <Box sx={{ mt: 3, width: "100%" }}>
        <Box
          id="study-coverage-heatmap-chart"
          sx={{
            width: "100%",
            minWidth: 1100,
            height: Math.max(560, 220 + metricLabels.length * 46),
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            backgroundColor: "#fff",
            p: 0,
            overflowX: "auto",
          }}
        >
          <ReactECharts
            option={option}
            style={{ width: "100%", height: "100%" }}
            opts={{ renderer: "canvas" }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="caption" color="textSecondary">
            Coverage scale:
          </Typography>
          <Box
            sx={{
              width: 220,
              height: 12,
              borderRadius: 999,
              background:
                "linear-gradient(90deg, rgb(215,48,39) 0%, rgb(255,152,80) 50%, rgb(26,150,65) 100%)",
              border: "1px solid #ddd",
            }}
          />
          <Typography variant="caption">0%</Typography>
          <Typography variant="caption">50%</Typography>
          <Typography variant="caption">100%</Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Coverage Heatmap
        </Typography>

        {/* Device Type Selector */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
            Device Type
          </Typography>
          <ToggleButtonGroup
            value={deviceType}
            exclusive
            onChange={(_, value) => {
              if (value) setDeviceType(value);
            }}
            size="small"
          >
            <ToggleButton value="fitbit" disabled={!hasFitbitParticipants}>
              Fitbit Only
            </ToggleButton>
            <ToggleButton
              value="healthkit"
              disabled={!hasHealthkitParticipants}
            >
              HealthKit Only
            </ToggleButton>
            <ToggleButton value="both" disabled={!canSelectBoth}>
              Both / Common Metrics
            </ToggleButton>
          </ToggleButtonGroup>

          {!hasFitbitParticipants && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 0.5 }}
            >
              No participant with Fitbit is enrolled in this study.
            </Typography>
          )}
          {!hasHealthkitParticipants && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 0.25 }}
            >
              No participant with HealthKit is enrolled in this study.
            </Typography>
          )}
        </Box>

        {/* Date Range & Presets */}
        <ParticipantCoverageFilters
          disabled={false}
          fromDate={fromDate}
          endDate={endDate}
          preset={preset}
          onFromDateChange={(value) => {
            setPreset(null);
            setFromDate(value);
          }}
          onEndDateChange={(value) => {
            setPreset(null);
            setEndDate(value);
          }}
          onPresetChange={applyPreset}
          onRun={handleGenerateHeatmap}
          running={heatmapRunning}
        />

        {/* Aggregation Type Selection */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Aggregation Level</InputLabel>
          <Select
            value={aggregationType}
            label="Aggregation Level"
            onChange={(e) =>
              setAggregationType(
                e.target.value as "daily" | "weekly" | "monthly" | "yearly",
              )
            }
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
          </Select>
        </FormControl>

        {/* Metric Selection Accordion */}
        <Accordion
          expanded={metricsExpanded}
          onChange={(_, expanded) => setMetricsExpanded(expanded)}
          disableGutters
          elevation={0}
          sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack spacing={0.25}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Select Metrics
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedMetrics.size} selected of {availableMetrics.length}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", gap: 1 }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={selectAll}
                  disabled={availableMetrics.length === 0}
                >
                  Select All
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={selectCommonOnly}
                  disabled={commonMetricCount === 0}
                >
                  Choose Common Metrics
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={deselectAll}
                  disabled={selectedMetrics.size === 0}
                >
                  Deselect All
                </Button>
              </Stack>

              <FormGroup
                sx={{
                  maxHeight: "220px",
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  p: 1,
                  borderRadius: 1,
                  columnCount: { xs: 1, md: 2, lg: 3 },
                  columnGap: { md: "24px", lg: "32px" },
                }}
              >
                {availableMetrics.map((metric) => (
                  <FormControlLabel
                    key={metric.key}
                    control={
                      <Checkbox
                        checked={selectedMetrics.has(metric.key)}
                        onChange={(e) =>
                          toggleMetric(metric.key, e.target.checked)
                        }
                      />
                    }
                    label={metric.label}
                    sx={{
                      fontSize: "0.9rem",
                      mr: 0,
                      mb: 0.5,
                      breakInside: "avoid",
                      display: "block",
                    }}
                  />
                ))}
              </FormGroup>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {selectedMetrics.size > 0 && (
          <Box sx={{ mt: -1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {Array.from(selectedMetrics).map((metricKey) => {
              const metric = availableMetrics.find((m) => m.key === metricKey);
              return (
                <Chip
                  key={metricKey}
                  label={metric?.label || metricKey}
                  size="small"
                  onDelete={() => toggleMetric(metricKey, false)}
                />
              );
            })}
          </Box>
        )}

        {/* Loading Progress */}
        {heatmapRunning && (
          <Stack spacing={1}>
            <LinearProgress variant="indeterminate" />
            <Typography variant="body2" color="textSecondary">
              {progressStatus || "Generating..."}
            </Typography>
          </Stack>
        )}

        {/* Error Display */}
        {heatmapError && <Alert severity="error">{heatmapError}</Alert>}

        {fetchStatus && <Alert severity="info">{fetchStatus}</Alert>}

        {failedFitbitRefreshes.length > 0 && (
          <Alert severity="warning">
            Fitbit data could not be retrieved for:{" "}
            {failedFitbitRefreshes
              .map((entry) => {
                const ranges = entry.ranges
                  .map((range) => `${range.startDate} to ${range.endDate}`)
                  .join(", ");
                return `${entry.participantName} (${ranges})`;
              })
              .join("; ")}
          </Alert>
        )}

        {/* Heatmap Display */}
        {heatmapData && renderHeatmap()}
      </Stack>
    </Paper>
  );
};

export default StudyLevelMetricCoverageHeatmapCard;
